"use client";

import { useEffect, useRef, useState } from "react";
import { useRealtimeKitMeeting, useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";

import { useClassroomStore } from "../store/useClassroomStore";
import { isScreenShareSupportedAsync } from "../utils/screenShareUtils";
import { getRecordingManager } from "../utils/recordingUtils";

import LeaveCallDialog from "./LeaveCallDialog";
import { ActivitiesDialog } from "./ActivitiesDialog";

import useUserStore from "@/features/auth/store/useUserStore.ts";
import PATHS from "@/routes/paths.ts";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export default function MobileControlsDialog() {
  const { t } = useTranslation(["common", "classroom"]);

  // Use Cloudflare Realtime Kit hooks instead of VideoSDK
  const { meeting } = useRealtimeKitMeeting();

  const navigate = useNavigate();
  const { workspaceId, lessonId } = useParams();
  const { user } = useUserStore();

  // Get local user's media state using Cloudflare selectors
  const isAudioEnabled = useRealtimeKitSelector((m) => m.self?.audioEnabled ?? false);
  const isVideoEnabled = useRealtimeKitSelector((m) => m.self?.videoEnabled ?? false);
  const isScreenSharing = useRealtimeKitSelector((m) => m.self?.screenShareEnabled ?? false);

  // Use store for device selection and other non-media state
  const {
    selectedWebcam,
    setSelectedWebcam,
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    presenterId,
    setShouldMaintainConnection,
    // Extension dialog
    extensionInfo,
    setExtensionDialogOpen,
    // Chat management
    isChatOpen,
    toggleChat,
    meetingId,
    // Recording state
    isRecording,
    setIsRecording,
    recordingStartTime,
    setRecordingStartTime,
    setRecordingBlob,
  } = useClassroomStore();

  // Device lists state
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivitiesDialogOpen, setIsActivitiesDialogOpen] = useState(false);

  // Track if device change is pending
  const [pendingMicChange, setPendingMicChange] = useState<string | null>(null);
  const micChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if screen sharing is supported on this device
  const [screenShareSupported, setScreenShareSupported] = useState<boolean>(false);

  // Check if recording is supported
  const [recordingSupported, setRecordingSupported] = useState<boolean>(false);

  // Get recording manager for status
  const recordingManager = getRecordingManager();

  // Track recording duration for mobile dialog
  const [recordingDuration, setRecordingDuration] = useState(0);

  // Computed property to check if screen sharing is active
  const isScreenSharingActive = presenterId !== null;
  const isLocalScreenSharing = isScreenSharing;

  // Format duration as MM:SS
  const formatDuration = (durationMs: number) => {
    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  // Update recording duration every second when recording
  useEffect(() => {
    if (!isRecording || !recordingStartTime) {
      setRecordingDuration(0);

      return;
    }

    const interval = setInterval(() => {
      const duration = Date.now() - recordingStartTime;

      setRecordingDuration(duration);
    }, 1000);

    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  const shouldShowExtendButton =
    user?.role === "tutor" &&
    extensionInfo &&
    extensionInfo.eventData.allow_extension &&
    extensionInfo.roomId === meetingId;

  // Function to fetch devices using Cloudflare API
  const fetchDevices = async () => {
    if (!meeting) return;

    try {
      const audioDevices = await meeting.self.getAudioDevices();
      const videoDevices = await meeting.self.getVideoDevices();
      const speakerDevices = await meeting.self.getSpeakerDevices();

      setAudioInputs(audioDevices);
      setVideoInputs(videoDevices);
      setAudioOutputs(speakerDevices);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  // Function to change speaker (audio output)
  const changeSpeakerDevice = async (deviceId: string) => {
    try {
      setSelectedSpeaker(deviceId);

      if (meeting) {
        const speakerDevice = audioOutputs.find((device) => device.deviceId === deviceId);

        if (speakerDevice) {
          await meeting.self.setDevice(speakerDevice);
          console.log("Speaker device changed to:", deviceId);
        }
      }
    } catch (error) {
      console.error("Error changing speaker device:", error);
    }
  };

  // Check screen share support on mount
  useEffect(() => {
    const checkScreenShareSupport = async () => {
      try {
        const supported = await isScreenShareSupportedAsync();

        setScreenShareSupported(supported);
      } catch (error) {
        console.error("Error checking screen share support:", error);
        setScreenShareSupported(false);
      }
    };

    checkScreenShareSupport();
  }, []);

  // Check recording support on mount
  useEffect(() => {
    const checkRecordingSupport = () => {
      const supported = recordingManager.isRecordingSupported();

      setRecordingSupported(supported);
    };

    checkRecordingSupport();
  }, [recordingManager]);

  // Fetch devices on mount and when meeting changes
  useEffect(() => {
    fetchDevices();
  }, [meeting]);

  // Apply the selected devices when the component mounts
  useEffect(() => {
    const applySelectedDevices = async () => {
      const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

      if (isTransitioningFromPiP || isPipMode) {
        console.log("Skipping device application - PiP mode or transitioning");

        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (
        selectedMic &&
        audioInputs.some((m) => m.deviceId === selectedMic) &&
        isAudioEnabled &&
        meeting
      ) {
        try {
          const device = audioInputs.find((m) => m.deviceId === selectedMic);

          if (device) {
            await meeting.self.setDevice(device);
          }
        } catch (error) {
          console.error("Error applying selected microphone:", error);
        }
      }

      if (
        selectedWebcam &&
        videoInputs.some((w) => w.deviceId === selectedWebcam) &&
        isVideoEnabled &&
        meeting
      ) {
        try {
          const device = videoInputs.find((w) => w.deviceId === selectedWebcam);

          if (device) {
            await meeting.self.setDevice(device);
          }
        } catch (error) {
          console.error("Error applying selected webcam:", error);
        }
      }

      if (selectedSpeaker && audioOutputs.some((s) => s.deviceId === selectedSpeaker)) {
        changeSpeakerDevice(selectedSpeaker);
      }
    };

    fetchDevices().then(() => {
      const { isJoined } = useClassroomStore.getState();

      if (isJoined) {
        applySelectedDevices();
      }
    });

    navigator.mediaDevices?.addEventListener("devicechange", fetchDevices);

    return () => {
      navigator.mediaDevices?.removeEventListener("devicechange", fetchDevices);
      if (micChangeTimeoutRef.current) {
        clearTimeout(micChangeTimeoutRef.current);
      }
    };
  }, []);

  // Also monitor when meeting is joined to apply devices
  const isJoined = useClassroomStore((state) => state.isJoined);
  const hasAppliedDevices = useRef(false);

  useEffect(() => {
    if (
      isJoined &&
      !hasAppliedDevices.current &&
      videoInputs.length > 0 &&
      audioInputs.length > 0
    ) {
      hasAppliedDevices.current = true;

      const applySelectedDevices = async () => {
        const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

        if (isTransitioningFromPiP || isPipMode) {
          console.log("Skipping device application after join - PiP mode or transitioning");

          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));

        if (
          selectedMic &&
          audioInputs.some((m) => m.deviceId === selectedMic) &&
          isAudioEnabled &&
          meeting
        ) {
          try {
            const device = audioInputs.find((m) => m.deviceId === selectedMic);

            if (device) {
              await meeting.self.setDevice(device);
            }
          } catch (error) {
            console.error("Error applying selected microphone after join:", error);
          }
        }

        if (
          selectedWebcam &&
          videoInputs.some((w) => w.deviceId === selectedWebcam) &&
          isVideoEnabled &&
          meeting
        ) {
          try {
            const device = videoInputs.find((w) => w.deviceId === selectedWebcam);

            if (device) {
              await meeting.self.setDevice(device);
            }
          } catch (error) {
            console.error("Error applying selected webcam after join:", error);
          }
        }

        if (selectedSpeaker && audioOutputs.some((s) => s.deviceId === selectedSpeaker)) {
          changeSpeakerDevice(selectedSpeaker);
        }
      };

      applySelectedDevices();
    }
  }, [
    isJoined,
    videoInputs,
    audioInputs,
    audioOutputs,
    selectedMic,
    selectedWebcam,
    selectedSpeaker,
    meeting,
  ]);

  const handleWebcamChange = async (deviceId: string) => {
    const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

    setSelectedWebcam(deviceId);

    if (isTransitioningFromPiP || isPipMode) {
      console.log("Skipping webcam change - PiP mode or transitioning, only updating selection");

      return;
    }

    try {
      const device = videoInputs.find((d) => d.deviceId === deviceId);

      if (device) {
        await meeting.self.setDevice(device);
      }
    } catch (error) {
      console.error("Error changing webcam:", error);
    }
  };

  const handleMicChange = async (deviceId: string) => {
    const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

    setSelectedMic(deviceId);

    if (isTransitioningFromPiP || isPipMode) {
      console.log("Skipping mic change - PiP mode or transitioning, only updating selection");

      return;
    }

    setPendingMicChange(deviceId);

    if (micChangeTimeoutRef.current) {
      clearTimeout(micChangeTimeoutRef.current);
    }

    micChangeTimeoutRef.current = setTimeout(async () => {
      try {
        const device = audioInputs.find((d) => d.deviceId === deviceId);

        if (device) {
          await meeting.self.setDevice(device);
        }
        console.log("changed mic to: ", device);
        setPendingMicChange(null);
      } catch (error) {
        console.error("Error changing microphone:", error);
        setPendingMicChange(null);
      }
    }, 300);
  };

  const handleScreenShare = async () => {
    if (meeting) {
      if (isLocalScreenSharing) {
        await meeting.self.disableScreenShare();
      } else {
        await meeting.self.enableScreenShare();
      }
    }
    setIsDialogOpen(false);
  };

  const handleLeaveClick = () => {
    setIsLeaveDialogOpen(true);
  };

  const handleLeaveConfirm = () => {
    setShouldMaintainConnection(false);

    try {
      meeting.leave();
    } catch (error) {
      console.error("Error leaving meeting:", error);
    }

    setIsLeaveDialogOpen(false);
    if (user && workspaceId && lessonId) {
      if (user.role === "tutor") {
        navigate(`${PATHS.TUTOR.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${lessonId}/details`);
      } else {
        navigate(
          `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${lessonId}/details`,
        );
      }
    }
  };

  const handleStartRecording = async () => {
    try {
      await recordingManager.startRecording();
      setIsRecording(true);
      setRecordingStartTime(Date.now());
      console.log("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const handleStopRecording = async () => {
    try {
      const blob = await recordingManager.stopRecording();

      setIsRecording(false);
      setRecordingStartTime(null);
      setRecordingBlob(blob);

      if (blob) {
        recordingManager.downloadRecording(blob, `lesson-recording-${Date.now()}`);
      }

      console.log("Recording stopped");
    } catch (error) {
      console.error("Error stopping recording:", error);
      setIsRecording(false);
      setRecordingStartTime(null);
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };

  const handleToggleMic = async () => {
    const newMicState = !isAudioEnabled;

    if (meeting) {
      try {
        if (newMicState) {
          await meeting.self.enableAudio();
        } else {
          await meeting.self.disableAudio();
        }
      } catch (error) {
        console.error("Error toggling mic:", error);
      }
    }
  };

  const handleToggleWebcam = async () => {
    const newWebcamState = !isVideoEnabled;

    if (meeting) {
      try {
        if (newWebcamState) {
          await meeting.self.enableVideo();
        } else {
          await meeting.self.disableVideo();
        }
      } catch (error) {
        console.error("Error toggling webcam:", error);
      }
    }
  };

  // Utility function to truncate device names
  const truncateDeviceName = (name: string, maxLength: number = 25) => {
    if (!name) return "Unknown Device";

    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
  };

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <motion.button
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-xl transition-all",
              "bg-side-accent/80 hover:bg-side-accent/60 backdrop-blur-sm",
              isRecording && "bg-destructive/80 hover:bg-destructive/60",
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon
              className={cn(
                "transition-colors",
                isRecording ? "text-primary" : "text-accent-foreground",
              )}
              height="1.25rem"
              icon={isRecording ? "lucide:square" : "lucide:settings"}
              width="1.25rem"
            />
          </motion.button>
        </DialogTrigger>

        <DialogContent className="bg-side-accent/80 text-accent-foreground h-dvh w-screen overflow-hidden p-0 shadow-2xl backdrop-blur-xl max-sm:rounded-none">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="from-accent-foreground/40 to-accent-foreground/60 absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br" />
            <div className="from-accent-foreground/30 to-accent-foreground/50 absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr" />
          </div>

          <div className="relative flex h-full max-w-full flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-side-accent/50 max-w-full p-4 backdrop-blur-sm">
              <motion.h2
                animate={{ opacity: 1, y: 0 }}
                className="text-accent-foreground truncate text-lg font-bold"
                initial={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {t("classroom:meetingControls")}
              </motion.h2>
            </div>

            {/* Recording Status */}
            {isRecording && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-destructive/10 flex max-w-full items-center justify-between px-4 py-3"
                initial={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    className="bg-destructive h-3 w-3 flex-shrink-0 rounded-full"
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY }}
                  />
                  <span className="text-destructive truncate text-sm font-semibold">
                    {t("classroom:recordingInProgress")}
                  </span>
                </div>
                <Badge className="flex-shrink-0 px-2 py-1 font-mono text-xs" variant="destructive">
                  {formatDuration(recordingDuration)}
                </Badge>
              </motion.div>
            )}

            {/* Main Content - Scrollable */}
            <ScrollArea className="max-w-full flex-1">
              <div className="max-w-full space-y-4 p-4">
                {/* Quick Controls Section */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent/50 max-w-full rounded-xl p-4 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mb-4 flex max-w-full items-center gap-3">
                    <div className="bg-accent-foreground/10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="lucide:zap"
                        width="1rem"
                      />
                    </div>
                    <h3 className="text-accent-foreground truncate text-sm font-semibold">
                      {t("common:controls" as any)}
                    </h3>
                  </div>

                  <div className="max-w-full space-y-3">
                    {/* Mic Control */}
                    <motion.button
                      className={cn(
                        "flex w-full max-w-full items-center justify-between rounded-lg p-3 transition-all",
                        isAudioEnabled
                          ? "bg-muted hover:bg-muted/80"
                          : "bg-destructive hover:bg-destructive/90",
                      )}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={handleToggleMic}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={cn(
                            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
                            isAudioEnabled ? "bg-accent-foreground/10" : "bg-destructive/20",
                          )}
                        >
                          <Icon
                            className={cn(
                              isAudioEnabled
                                ? "text-accent-foreground"
                                : "text-destructive-foreground",
                            )}
                            height="1.25rem"
                            icon={isAudioEnabled ? "lucide:mic" : "lucide:mic-off"}
                            width="1.25rem"
                          />
                        </div>
                        <span className="truncate text-sm font-medium">
                          {isAudioEnabled ? t("classroom:muteMic") : t("classroom:unmuteMic")}
                        </span>
                      </div>
                    </motion.button>

                    {/* Camera Control */}
                    <motion.button
                      className={cn(
                        "flex w-full max-w-full items-center justify-between rounded-lg p-3 transition-all",
                        isVideoEnabled
                          ? "bg-muted hover:bg-muted/80"
                          : "bg-destructive hover:bg-destructive/90",
                      )}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={handleToggleWebcam}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={cn(
                            "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
                            isVideoEnabled ? "bg-accent-foreground/10" : "bg-destructive/20",
                          )}
                        >
                          <Icon
                            className={cn(
                              isVideoEnabled
                                ? "text-accent-foreground"
                                : "text-destructive-foreground",
                            )}
                            height="1.25rem"
                            icon={isVideoEnabled ? "lucide:video" : "lucide:video-off"}
                            width="1.25rem"
                          />
                        </div>
                        <span className="truncate text-sm font-medium">
                          {isVideoEnabled
                            ? t("classroom:disableCamera")
                            : t("classroom:enableCamera")}
                        </span>
                      </div>
                    </motion.button>

                    {/* Screen Share Control */}
                    {screenShareSupported && (
                      <motion.button
                        className={cn(
                          "flex w-full max-w-full items-center justify-between rounded-lg p-3 transition-all",
                          isLocalScreenSharing
                            ? "bg-primary hover:bg-primary/90"
                            : isScreenSharingActive
                              ? "bg-muted/50 cursor-not-allowed opacity-60"
                              : "bg-muted hover:bg-muted/80",
                        )}
                        disabled={isScreenSharingActive && !isLocalScreenSharing}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={handleScreenShare}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={cn(
                              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
                              isLocalScreenSharing
                                ? "bg-primary-foreground/20"
                                : "bg-accent-foreground/10",
                            )}
                          >
                            <Icon
                              className={cn(
                                isLocalScreenSharing
                                  ? "text-primary-foreground"
                                  : "text-accent-foreground",
                              )}
                              height="1.25rem"
                              icon={isLocalScreenSharing ? "lucide:monitor-off" : "lucide:monitor"}
                              width="1.25rem"
                            />
                          </div>
                          <span className="truncate text-sm font-medium">
                            {isLocalScreenSharing
                              ? t("classroom:stopScreenShare")
                              : t("classroom:screenShare")}
                          </span>
                        </div>
                      </motion.button>
                    )}

                    {/* Recording Control */}
                    {recordingSupported && (
                      <motion.button
                        className={cn(
                          "flex w-full max-w-full items-center justify-between rounded-lg p-3 transition-all",
                          isRecording
                            ? "bg-destructive hover:bg-destructive/90"
                            : "bg-muted hover:bg-muted/80",
                        )}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={handleToggleRecording}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={cn(
                              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg",
                              isRecording ? "bg-destructive/20" : "bg-accent-foreground/10",
                            )}
                          >
                            <Icon
                              className={cn(
                                isRecording
                                  ? "text-destructive-foreground"
                                  : "text-accent-foreground",
                              )}
                              height="1.25rem"
                              icon={isRecording ? "lucide:square" : "lucide:circle"}
                              width="1.25rem"
                            />
                          </div>
                          <span className="truncate text-sm font-medium">
                            {isRecording
                              ? t("classroom:stopRecording")
                              : t("classroom:startRecording")}
                          </span>
                        </div>
                      </motion.button>
                    )}
                  </div>
                </motion.div>

                {/* Additional Actions Section */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent/50 max-w-full rounded-xl p-4 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mb-4 flex max-w-full items-center gap-3">
                    <div className="bg-accent-foreground/10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="lucide:more-horizontal"
                        width="1rem"
                      />
                    </div>
                    <h3 className="text-accent-foreground truncate text-sm font-semibold">
                      {t("common:actions" as any)}
                    </h3>
                  </div>

                  <div className="max-w-full space-y-3">
                    {/* Chat */}
                    <motion.button
                      className={cn(
                        "bg-muted hover:bg-muted/80 flex w-full max-w-full items-center justify-between rounded-lg px-3 py-3 text-left transition-all",
                        isChatOpen
                          ? "bg-primary hover:bg-primary/90"
                          : "bg-muted hover:bg-muted/80",
                      )}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={toggleChat}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="bg-accent-foreground/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                          <Icon
                            className={cn(
                              isChatOpen ? "text-primary-foreground" : "text-accent-foreground",
                            )}
                            height="1.25rem"
                            icon={
                              isChatOpen ? "lucide:message-square-text" : "lucide:message-square"
                            }
                            width="1.25rem"
                          />
                        </div>
                        <span className="truncate text-sm font-medium">
                          {t("classroom:lessonChat")}
                        </span>
                      </div>
                    </motion.button>

                    {/* Extend Lesson */}
                    {shouldShowExtendButton && (
                      <motion.button
                        className="bg-accent-foreground hover:bg-accent-foreground/75 flex w-full max-w-full items-center justify-between rounded-lg px-3 py-3 text-left transition-all"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setExtensionDialogOpen(true)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="bg-primary/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                            <Icon
                              className="text-primary"
                              height="1.25rem"
                              icon="lucide:clock"
                              width="1.25rem"
                            />
                          </div>
                          <span className="text-primary truncate text-sm font-medium">
                            {t("classroom:extendLesson")}
                          </span>
                        </div>
                        <Icon
                          className="text-primary flex-shrink-0"
                          height="1rem"
                          icon="lucide:chevron-right"
                          width="1rem"
                        />
                      </motion.button>
                    )}

                    {/* Activities */}
                    {user?.role === "tutor" && (
                      <motion.button
                        className="bg-muted hover:bg-muted/80 flex w-full max-w-full items-center justify-between rounded-lg px-3 py-3 text-left transition-all"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setIsActivitiesDialogOpen(true)}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="bg-accent-foreground/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                            <Icon
                              className="text-primary-foreground"
                              height="1.25rem"
                              icon="lucide:activity"
                              width="1.25rem"
                            />
                          </div>
                          <span className="text-accent-foreground truncate text-sm font-medium">
                            {t("classroom:activities")}
                          </span>
                        </div>
                        <Icon
                          className="text-muted-foreground flex-shrink-0"
                          height="1rem"
                          icon="lucide:chevron-right"
                          width="1rem"
                        />
                      </motion.button>
                    )}

                    {/* Leave Call */}
                    <motion.button
                      className="bg-destructive hover:bg-destructive/90 flex w-full max-w-full items-center justify-between rounded-lg px-3 py-3 text-left transition-all"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={handleLeaveClick}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="bg-destructive/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
                          <Icon
                            className="text-destructive-foreground"
                            height="1.25rem"
                            icon="lucide:phone-off"
                            width="1.25rem"
                          />
                        </div>
                        <span className="text-destructive-foreground truncate text-sm font-medium">
                          {t("classroom:leaveCall")}
                        </span>
                      </div>
                    </motion.button>
                  </div>
                </motion.div>

                {/* Device Settings Section */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent/50 max-w-full rounded-xl p-4 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mb-4 flex max-w-full items-center gap-3">
                    <div className="bg-accent-foreground/10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="lucide:settings"
                        width="1rem"
                      />
                    </div>
                    <h3 className="text-accent-foreground truncate text-sm font-semibold">
                      {t("classroom:deviceSettings")}
                    </h3>
                  </div>

                  <div className="max-w-full space-y-4">
                    {/* Microphone Selection */}
                    <div className="max-w-full">
                      <div className="mb-3 flex items-center gap-2">
                        <Icon
                          className="text-muted-foreground flex-shrink-0"
                          height="1rem"
                          icon="lucide:mic"
                          width="1rem"
                        />
                        <span className="text-accent-foreground truncate text-sm font-medium">
                          {t("classroom:selectMicrophone")}
                        </span>
                      </div>
                      <Select value={selectedMic || ""} onValueChange={handleMicChange}>
                        <SelectTrigger className="bg-background/30 text-accent-foreground w-full max-w-full">
                          <SelectValue placeholder={t("common:selectDevice" as any)} />
                        </SelectTrigger>
                        <SelectContent className="bg-side-accent/90 backdrop-blur-xl">
                          {audioInputs.length > 0 ? (
                            audioInputs.map((mic) => (
                              <SelectItem
                                key={mic.deviceId}
                                className="text-accent-foreground"
                                value={mic.deviceId}
                              >
                                <div className="flex max-w-full items-center gap-2">
                                  <Icon
                                    className="flex-shrink-0"
                                    height="1rem"
                                    icon="lucide:mic"
                                    width="1rem"
                                  />
                                  <span className="truncate">{truncateDeviceName(mic.label)}</span>
                                  {pendingMicChange === mic.deviceId && (
                                    <Icon
                                      className="flex-shrink-0 animate-spin"
                                      height="0.75rem"
                                      icon="lucide:loader-2"
                                      width="0.75rem"
                                    />
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem disabled className="text-muted-foreground" value="none">
                              {t("classroom:noMicrophonesFound")}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Camera Selection */}
                    <div className="max-w-full">
                      <div className="mb-3 flex items-center gap-2">
                        <Icon
                          className="text-muted-foreground flex-shrink-0"
                          height="1rem"
                          icon="lucide:video"
                          width="1rem"
                        />
                        <span className="text-accent-foreground truncate text-sm font-medium">
                          {t("classroom:selectCamera")}
                        </span>
                      </div>
                      <Select value={selectedWebcam || ""} onValueChange={handleWebcamChange}>
                        <SelectTrigger className="bg-background/30 text-accent-foreground w-full max-w-full">
                          <SelectValue placeholder={t("common:selectDevice" as any)} />
                        </SelectTrigger>
                        <SelectContent className="bg-side-accent/90 backdrop-blur-xl">
                          {videoInputs.length > 0 ? (
                            videoInputs.map((camera) => (
                              <SelectItem
                                key={camera.deviceId}
                                className="text-accent-foreground"
                                value={camera.deviceId}
                              >
                                <div className="flex max-w-full items-center gap-2">
                                  <Icon
                                    className="flex-shrink-0"
                                    height="1rem"
                                    icon="lucide:video"
                                    width="1rem"
                                  />
                                  <span className="truncate">
                                    {truncateDeviceName(camera.label)}
                                  </span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem disabled className="text-muted-foreground" value="none">
                              {t("classroom:noCamerasFound")}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Speaker Selection */}
                    <div className="max-w-full">
                      <div className="mb-3 flex items-center gap-2">
                        <Icon
                          className="text-muted-foreground flex-shrink-0"
                          height="1rem"
                          icon="lucide:volume-2"
                          width="1rem"
                        />
                        <span className="text-accent-foreground truncate text-sm font-medium">
                          {t("common:speaker" as any)}
                        </span>
                      </div>
                      <Select value={selectedSpeaker || ""} onValueChange={changeSpeakerDevice}>
                        <SelectTrigger className="bg-background/30 text-accent-foreground w-full max-w-full">
                          <SelectValue placeholder={t("common:selectDevice" as any)} />
                        </SelectTrigger>
                        <SelectContent className="bg-side-accent/90 backdrop-blur-xl">
                          {audioOutputs.length > 0 ? (
                            audioOutputs.map((speaker) => (
                              <SelectItem
                                key={speaker.deviceId}
                                className="text-accent-foreground"
                                value={speaker.deviceId}
                              >
                                <div className="flex max-w-full items-center gap-2">
                                  <Icon
                                    className="flex-shrink-0"
                                    height="1rem"
                                    icon="lucide:volume-2"
                                    width="1rem"
                                  />
                                  <span className="truncate">
                                    {truncateDeviceName(speaker.label)}
                                  </span>
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem disabled className="text-muted-foreground" value="none">
                              {t("common:noSpeakersFound" as any)}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </motion.div>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Call Dialog */}
      <LeaveCallDialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
        onConfirm={handleLeaveConfirm}
      />

      {/* Activities Dialog */}
      <ActivitiesDialog
        isOpen={isActivitiesDialogOpen}
        onClose={() => setIsActivitiesDialogOpen(false)}
      />
    </>
  );
}
